import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { changePassword, type AuthError } from "@/lib/authv2/api";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const minPasswordLength = 8;

// Form nằm trong component riêng, chỉ mount khi dialog mở: Radix Portal gỡ
// nội dung lúc đóng nên state tự về rỗng, không cần effect reset.
export function ChangePasswordDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Đổi mật khẩu</DialogTitle>
        </DialogHeader>
        <ChangePasswordForm onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordForm({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (newPassword.length < minPasswordLength) {
      setError("Mật khẩu mới cần ít nhất 8 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Xác nhận mật khẩu chưa khớp.");
      return;
    }
    setSaving(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Đã đổi mật khẩu");
      onOpenChange(false);
    } catch (err) {
      const authError = err as AuthError;
      const message = changePasswordMessage(authError.code);
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  return (
    <form className="grid gap-4" onSubmit={submit}>
      <div className="grid gap-2">
        <Label htmlFor="current-password">Mật khẩu hiện tại</Label>
        <Input
          id="current-password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="new-password">Mật khẩu mới</Label>
        <Input
          id="new-password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="confirm-password">Xác nhận mật khẩu mới</Label>
        <Input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          required
        />
      </div>
      {error && (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
          Hủy
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? "Đang lưu..." : "Lưu"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function changePasswordMessage(code?: string) {
  switch (code) {
    case "invalid_current_password":
      return "Mật khẩu hiện tại không đúng.";
    case "new_password_too_short":
      return "Mật khẩu mới cần ít nhất 8 ký tự.";
    case "new_password_too_long":
      return "Mật khẩu mới quá dài.";
    case "new_password_same_as_current":
      return "Mật khẩu mới cần khác mật khẩu hiện tại.";
    case "casdoor_user_not_found":
      return "Tài khoản chưa được liên kết Casdoor.";
    case "identity_provider_unavailable":
      return "Casdoor đang tạm thời không sẵn sàng.";
    default:
      return "Không thể đổi mật khẩu.";
  }
}
